/**
 * Utility function to trap keyboard focus within a modal dialog.
 *
 * This function ensures that when the modal is open, keyboard navigation using the TAB key
 * cycles only through the focusable elements inside the modal, preventing focus from moving
 * outside the modal window.
 *
 * @param {HTMLElement} element - The modal container element to trap focus within.
 *
 * Usage example:
 *   const modal = document.getElementById('myModal');
 *   trapFocusOnModal(modal);
 *
 * Accessibility:
 *   Improves accessibility by keeping keyboard users within the modal until it is closed.
 */
const isVisible = el => {
  if (!el) return false;
  const style = window.getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden') return false;
  if (el.offsetParent === null && style.position !== 'fixed') return false;
  return true;
};

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([type="hidden"]):not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'object',
  'embed',
  'audio[controls]',
  'video[controls]',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
  '[role="button"][tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Returns the live list of focusable+visible elements inside the container.
 * Must be called at each keydown to reflect dynamic content changes.
 * The container itself may have tabindex="-1" (set by trapFocusOnModal),
 * so we only exclude children whose [tabindex="-1"] ancestor is NOT the container.
 */
const getLiveFocusableEls = (container) =>
  Array.from(container.querySelectorAll(FOCUSABLE_SELECTORS)).filter(el => {
    const trap = el.closest('[tabindex="-1"]');
    if (trap && trap !== container) return false;
    return isVisible(el);
  });

/**
 * @param {HTMLElement} element - The modal container element to trap focus within.
 * @param {HTMLElement} [initialFocusEl] - Optional element to focus on open instead of the first focusable.
 */
export const trapFocusOnModal = (element, initialFocusEl) => {
  // Store the previous tabindex so we can restore it on cleanup.
  const previousTabIndex = element.hasAttribute('tabindex')
    ? element.getAttribute('tabindex')
    : null;
  element.setAttribute('tabindex', '-1');
  const KEYCODE_TAB = 9;

  // Focus the explicitly provided element, the first focusable, or the container as fallback.
  if (initialFocusEl instanceof HTMLElement && typeof initialFocusEl.focus === 'function') {
    initialFocusEl.focus();
  } else {
    const initialFocusableEls = getLiveFocusableEls(element);
    if (initialFocusableEls.length) {
      initialFocusableEls[0].focus();
    } else {
      element.focus();
    }
  }

  const keyboardNavigation = (e) => {
    const isTabPressed = e.key === 'Tab' || e.keyCode === KEYCODE_TAB;
    if (!isTabPressed) {
      return;
    }

    // Recalculate every time: dynamic content may have changed since the trap was set.
    const focusableEls = getLiveFocusableEls(element);
    if (!focusableEls.length) {
      e.preventDefault();
      return;
    }

    const firstFocusableEl = focusableEls[0];
    const lastFocusableEl = focusableEls[focusableEls.length - 1];

    if (e.shiftKey) {
      /* shift + tab */
      if (document.activeElement === firstFocusableEl || document.activeElement === element) {
        lastFocusableEl.focus();
        e.preventDefault();
      }
    } else {
      /* tab */
      if (document.activeElement === lastFocusableEl || document.activeElement === element) {
        firstFocusableEl.focus();
        e.preventDefault();
      }
    }
  };

  element.addEventListener('keydown', keyboardNavigation);

  // Restituisci handler e il valore precedente di tabindex per poterli ripristinare successivamente
  return { keyboardNavigation, previousTabIndex };
};

export const removeTrapFocusOnModal = (element, trapFocusResult) => {
  if (!element) return;
  const keyboardNavigation = trapFocusResult && trapFocusResult.keyboardNavigation;
  const hasPreviousTabIndex =
    !!trapFocusResult &&
    Object.prototype.hasOwnProperty.call(trapFocusResult, 'previousTabIndex');
  if (hasPreviousTabIndex) {
    const { previousTabIndex } = trapFocusResult;
    if (previousTabIndex !== null && previousTabIndex !== undefined) {
      element.setAttribute('tabindex', previousTabIndex);
    } else {
      element.removeAttribute('tabindex');
    }
  }
  if (keyboardNavigation) {
    element.removeEventListener('keydown', keyboardNavigation);
  }
};
